import useCustomFieldsHook from "@/hooks/use-custom-fields.hook.tsx";
import useEmployeeCustomFieldsHook from "@/hooks/use-employee-custom-fields.hook.tsx";
import usePermissions from "@/hooks/use-permissions.hook.ts";
import { EmployeePageSection } from "@/pages/employees/employee/employee-page.constants.ts";
import { useAppConfig } from "@/queries/settings/use-app.query.ts";
import { ApiCFTabType, ApiSectionTypes, ApiTableTypes } from "@/types/custom-field.types.ts";
import { ApiEmployee } from "@/types/employees/employee.types.ts";
import { ApiFeatureType } from "@/types/subscription.types.ts";
import {
  Box,
  BoxWithScroll,
  ButtonIcon,
  DropdownMenu,
  DropdownMenuItem,
  FontAwesomeIcon,
  Input,
  Stack,
  Text,
  Title,
} from "@skailer-com/uikit";
import { useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useLocation, useNavigate } from "react-router-dom";

interface SearchResult {
  id?: string;
  title: string;
  path?: string;
  section: string;
  tabType: ApiCFTabType;
}

interface Props {
  employee: ApiEmployee;
}

const getSectionAnchor = (id: number) => `section-${id}`;
const getTableAnchor = (id: number) => `table-${id}`;
const getFieldAnchor = (id: number) => `field-${id}`;

export const EmployeeProfileSearch = ({ employee }: Props) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const { data: config } = useAppConfig();
  const { getSectionTitle, getTableTitle, getFieldTitle, getTabTitle } = useCustomFieldsHook();
  const { employeeCustomFieldCanView } = useEmployeeCustomFieldsHook();
  const { isFeatureEnabled, canViewCustomTableFieldForEmployee, canViewCustomTableForEmployee } =
    usePermissions();
  const isCustomFieldsEnabled = isFeatureEnabled(ApiFeatureType.custom_fields);
  const [isOpen, setIsOpen] = useState(false);
  const [query, setQuery] = useState("");
  const inputRef = useRef<HTMLInputElement>(null);

  const searchIndex = useMemo(() => {
    const sections: SearchResult[] = [];
    const fields: SearchResult[] = [];

    config?.ui.employee_profile.tabs.forEach(({ tab, items }) => {
      const tabTitle = getTabTitle(tab);

      if (tab.type !== ApiCFTabType.sidebar && tabTitle) {
        sections.push({
          title: tabTitle,
          section: tabTitle,
          tabType: tab.type,
        });
      }

      items.forEach((item) => {
        if (item.section) {
          const sectionConfig = item.section;
          if (
            sectionConfig.section.section_type === ApiSectionTypes.custom &&
            !isCustomFieldsEnabled
          ) {
            return;
          }

          const sectionTitle = getSectionTitle(sectionConfig.section);
          const visibleFields = sectionConfig.fields.filter((field) =>
            employeeCustomFieldCanView(field, employee)
          );

          if (visibleFields.length > 0) {
            sections.push({
              id: getSectionAnchor(sectionConfig.section.id),
              title: sectionTitle,
              section: sectionTitle,
              tabType: tab.type,
            });
          }

          visibleFields.forEach((field) => {
            fields.push({
              id:
                sectionConfig.section.section_type === ApiSectionTypes.custom ||
                tab.type === ApiCFTabType.sidebar
                  ? getFieldAnchor(field.id)
                  : getSectionAnchor(sectionConfig.section.id),
              title: getFieldTitle(field),
              section: sectionTitle,
              tabType: tab.type,
            });
          });
        }

        if (item.table) {
          const table = item.table;
          if (table.table_type === ApiTableTypes.custom && !isCustomFieldsEnabled) return;

          const hasTableAccess =
            table.table_type !== ApiTableTypes.custom ||
            canViewCustomTableForEmployee(employee, table.id);
          if (!hasTableAccess) return;

          const tableTitle = getTableTitle(table);
          sections.push({
            id: getTableAnchor(table.id),
            title: tableTitle,
            section: tableTitle,
            tabType: tab.type,
          });
          table.table_fields
            .filter(
              (field) =>
                (table.table_type === ApiTableTypes.custom &&
                  canViewCustomTableFieldForEmployee(employee, table.id, field.slug)) ||
                employeeCustomFieldCanView(field, employee)
            )
            .forEach((field) => {
              fields.push({
                id: getTableAnchor(table.id),
                title: getFieldTitle(field),
                path: `${getTabTitle(tab)} → ${tableTitle}`,
                section: tableTitle,
                tabType: tab.type,
              });
            });
        }
      });
    });

    return { sections, fields };
  }, [
    config,
    canViewCustomTableFieldForEmployee,
    canViewCustomTableForEmployee,
    employee,
    employeeCustomFieldCanView,
    getFieldTitle,
    getSectionTitle,
    getTableTitle,
    getTabTitle,
    isCustomFieldsEnabled,
  ]);

  const normalizedQuery = query.trim().toLocaleLowerCase();
  const results = useMemo(() => {
    if (!normalizedQuery) return { sections: [], fields: [] };

    const matches = (result: SearchResult) =>
      result.title.toLocaleLowerCase().includes(normalizedQuery);

    return {
      sections: searchIndex.sections.filter(matches),
      fields: searchIndex.fields.filter(matches),
    };
  }, [normalizedQuery, searchIndex]);

  useEffect(() => {
    if (!isOpen) return;
    requestAnimationFrame(() => inputRef.current?.focus());
  }, [isOpen]);

  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if ((event.metaKey || event.ctrlKey) && event.key.toLocaleLowerCase() === "f") {
        event.preventDefault();
        setIsOpen(true);
      }
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, []);

  const openResult = (result: SearchResult) => {
    const routeSection =
      result.tabType === ApiCFTabType.work_info ? EmployeePageSection.workInfo : undefined;
    const pathname =
      result.tabType === ApiCFTabType.sidebar
        ? location.pathname
        : `/employees/${employee.id}${routeSection ? `/${routeSection}` : ""}`;
    setIsOpen(false);
    navigate({ pathname, hash: result.id ? `#${result.id}` : "" });
  };

  const highlightMatches = (text: string) => {
    if (!normalizedQuery) return text;

    const normalizedText = text.toLocaleLowerCase();
    const parts = [];
    let startIndex = 0;
    let matchIndex = normalizedText.indexOf(normalizedQuery);

    while (matchIndex !== -1) {
      if (matchIndex > startIndex) parts.push(text.slice(startIndex, matchIndex));
      parts.push(
        <strong key={`${matchIndex}-${text}`} className={"font-semibold"}>
          {text.slice(matchIndex, matchIndex + normalizedQuery.length)}
        </strong>
      );
      startIndex = matchIndex + normalizedQuery.length;
      matchIndex = normalizedText.indexOf(normalizedQuery, startIndex);
    }

    if (startIndex < text.length) parts.push(text.slice(startIndex));
    return parts;
  };

  const renderGroup = (title: string, results: SearchResult[], withDivider = false) => {
    if (!results.length) return null;

    return (
      <Stack gap={"xs"} className={"px-2"}>
        {withDivider && <Box className={"border-t m-2 border-dark/10"} />}
        <Box className={"py-1 px-2 text-secondary"}>
          <Title header={`${title} (${results.length})`} size={"xs"} className={"text-secondary"} />
        </Box>
        {results.map((result, index) => (
          <DropdownMenuItem
            key={`${result.tabType}-${result.id ?? "tab"}-${index}`}
            onClick={() => openResult(result)}
            title={highlightMatches(result.title)}
            description={
              result.path && <Text className="text-sm text-secondary">{result.path}</Text>
            }
          />
        ))}
      </Stack>
    );
  };

  return (
    <DropdownMenu
      trigger={
        <ButtonIcon
          icon={<FontAwesomeIcon icon="fa-light fa-magnifying-glass" />}
          variant="secondary"
          title={t("common:search_in_profile")}
        />
      }
      open={isOpen}
      onOpenChange={setIsOpen}
      placement="bottom-start"
      closeOnClick={false}
    >
      <Stack className={"w-68 -m-2"}>
        <Box className={"px-4"}>
          <Input
            ref={inputRef}
            borderless
            value={query}
            onChange={(event) => setQuery(event.target.value)}
            onKeyDown={(event) => {
              if (event.key === "ArrowDown" || event.key === "ArrowUp") {
                event.preventDefault();
                return;
              }

              if (event.key !== "Escape") event.stopPropagation();
            }}
            label={t("common:search_in_profile_placeholder")}
          />
        </Box>

        {(results.sections.length > 0 || results.fields.length > 0) && (
          <BoxWithScroll className={"border-t max-h-100"}>
            <Stack className={"py-2"}>
              {renderGroup(t("custom_fields:sections"), results.sections)}
              {renderGroup(t("custom_fields:fields"), results.fields, results.sections.length > 0)}
            </Stack>
          </BoxWithScroll>
        )}

        {normalizedQuery && results.sections.length === 0 && results.fields.length === 0 && (
          <Box className={"border-t px-4 py-3"}>
            {t("common:no_results_for", { query: query.trim() })}
          </Box>
        )}
      </Stack>
    </DropdownMenu>
  );
};
